import React, { ReactNode, useRef } from 'react'
import { useMount } from 'react-use'

import LoadingWidget from '@/components/widgets/LoadingWidget'
import useAppDispatch from '@/hooks/useAppDispatch'
import useAppSelector from '@/hooks/useAppSelector'
import { useCommonDataQuery } from '@/hooks/useCommonDataQuery'
import { useCreateSessionMetadataMutation } from '@/hooks/useCreateSessionMetadataMutation'
import { useIsPdf } from '@/hooks/useIsPdf'
import authService, {
  AuthSessionIsAuthenticated,
} from '@/lib/services/authService'
import errorTypeService from '@/lib/services/errorTypeService'
import { selectIsCommonDataLoaded } from '@/redux/slices/commonData'
import { setIsAuthenticated } from '@/redux/slices/session'

interface Props {
  children: React.ReactNode
  /** A fallback react tree to show when loading the session, similar to how Suspense works */
  fallback?: ReactNode
}

const SessionProvider: React.FC<Props> = ({ children, fallback }) => {
  const initialiseSessionMutation = useCreateSessionMetadataMutation()
  const initialiseSessionMutationRef = useRef(initialiseSessionMutation)
  const commonDataQuery = useCommonDataQuery()
  const isPdf = useIsPdf()
  const dispatch = useAppDispatch()
  const isCommonDataLoaded = useAppSelector(selectIsCommonDataLoaded)

  // Listen for session authentication events and update Redux accordingly
  authService.addEventListener('sessionIsAuthenticated', (event: Event) => {
    const customEvent = event as CustomEvent<AuthSessionIsAuthenticated>
    const status = customEvent.detail.status

    dispatch(setIsAuthenticated(status))
  })

  // Initialise the session on mount
  useMount(() => {
    if (!isPdf) {
      initialiseSessionMutationRef.current.mutate()
    }
  })

  // The registration-blocked error must surface before the isLoading gate:
  // common data also 401s for a blocked registration, so isCommonDataLoaded
  // never becomes true and the spinner would otherwise show forever.
  if (
    errorTypeService.isRegistrationBlockedError(initialiseSessionMutation.error)
  ) {
    throw initialiseSessionMutation.error
  }

  const error = initialiseSessionMutation.error || commonDataQuery.error

  // A failed common data request means isCommonDataLoaded never becomes true,
  // so it must stop the spinner and show the error screen instead. Wait for
  // the session request first so its error takes priority.
  const isLoading =
    initialiseSessionMutation.isPending || (!isCommonDataLoaded && !error)

  if (isLoading) {
    return fallback || <LoadingWidget animateFromLogo />
  }

  if (error) {
    throw error
  }

  return <>{children}</>
}

export default SessionProvider
